import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import Loader from '../../components/common/Loader';
import Pagination from '../../components/common/Pagination';
import ConfirmActionModal from '../../components/modals/ConfirmActionModal';
import { PERMISSIONS, canFeatureForRole } from '../../utils/permissions';
import { useRolePermissionMatrix } from '../../hooks/useRolePermissionMatrix';
import { getDefaultItemsPerPageSync, getDefaultItemsPerPage } from '../../utils/settings';
import '../../styles/Table.css';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const statusBadge = (text) => (
  <span
    style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: '12px',
      background: '#edf2f7',
      color: '#4a5568',
      fontSize: '12px',
      fontWeight: 600,
    }}
  >
    {text || 'N/A'}
  </span>
);

const TYPE_BADGE = {
  asset: { bg: '#ebf8ff', color: '#2b6cb0', label: 'Asset' },
  project: { bg: '#faf5ff', color: '#6b46c1', label: 'Project' },
  task: { bg: '#f0fff4', color: '#276749', label: 'Task' },
};

const typeBadge = (type) => {
  const b = TYPE_BADGE[type] || { bg: '#edf2f7', color: '#4a5568', label: type };
  return (
    <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: '12px', background: b.bg, color: b.color, fontSize: '12px', fontWeight: 600 }}>
      {b.label}
    </span>
  );
};

// Per-type configuration: how to fetch, restore, permanently delete, and label each item type.
const TYPE_META = {
  asset: {
    label: 'Asset',
    labelPlural: 'Assets',
    permission: PERMISSIONS.DELETE_ASSETS,
    itemNoun: 'asset',
    fetch: (params) => api.getDeletedAssets(params),
    restore: (id) => api.restoreAsset(id),
    remove: (id) => api.permanentlyDeleteAsset(id),
    getTitle: (a) => a.assetTag || 'this asset',
    getDescription: (a) => (a.description ? `${a.assetTag || ''}${a.assetTag ? ' — ' : ''}${a.description}` : a.assetTag || '—'),
  },
  project: {
    label: 'Project',
    labelPlural: 'Projects',
    permission: PERMISSIONS.DELETE_PROJECT,
    itemNoun: 'project',
    fetch: (params) => api.getDeletedProjects(params),
    restore: (id) => api.restoreProject(id),
    remove: (id) => api.permanentlyDeleteProject(id),
    getTitle: (p) => p.name || 'this project',
    getDescription: (p) => p.name || '—',
  },
  task: {
    label: 'Task',
    labelPlural: 'Tasks',
    permission: PERMISSIONS.DELETE_TASK,
    itemNoun: 'task',
    fetch: (params) => api.getDeletedTasks(params),
    restore: (id) => api.restoreTask(id),
    remove: (id) => api.permanentlyDeleteTask(id),
    getTitle: (t) => t.name || 'this task',
    getDescription: (t) => `${t.taskCode ? `${t.taskCode} · ` : ''}${t.name || '—'}`,
  },
};

const TYPE_ORDER = ['asset', 'project', 'task'];

const TrashPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [allItems, setAllItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(getDefaultItemsPerPageSync());

  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToAction, setItemToAction] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [showEmptyTrashModal, setShowEmptyTrashModal] = useState(false);
  const [emptying, setEmptying] = useState(false);

  // Project restore-with-task-selection modal
  const [showProjectRestoreModal, setShowProjectRestoreModal] = useState(false);
  const [projectTrashedTasks, setProjectTrashedTasks] = useState([]);
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [loadingProjectTasks, setLoadingProjectTasks] = useState(false);

  const { rolePermissions, permissionsReady } = useRolePermissionMatrix();
  const normalizedUserRole = useMemo(() => {
    try {
      const u = JSON.parse(sessionStorage.getItem('user') || '{}');
      return String(u?.role || '').trim().toLowerCase();
    } catch (error) {
      return '';
    }
  }, []);

  // Which item types the user is allowed to see, based on their delete permissions.
  const allowedTypes = useMemo(() => {
    return TYPE_ORDER.filter((type) =>
      canFeatureForRole(normalizedUserRole, rolePermissions, [TYPE_META[type].permission])
    );
  }, [normalizedUserRole, rolePermissions]);

  const allowTrashPage = allowedTypes.length > 0;

  useEffect(() => {
    if (!permissionsReady) return;
    if (!allowTrashPage) {
      toast.error('You do not have permission to view trash');
      navigate('/admin/dashboard', { replace: true });
    }
  }, [permissionsReady, allowTrashPage, navigate]);

  // Fetch settings on mount and update itemsPerPage if needed.
  useEffect(() => {
    const updateItemsPerPage = async () => {
      const defaultItems = await getDefaultItemsPerPage();
      if (defaultItems !== itemsPerPage) {
        setItemsPerPage(defaultItems);
      }
    };
    updateItemsPerPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchTimerRef = useRef(null);

  // Fetch when type filter, search, or permissions change.
  useEffect(() => {
    if (!permissionsReady || !allowTrashPage) return;
    fetchTrash();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, searchTerm, permissionsReady, allowTrashPage, allowedTypes]);

  const fetchTrash = async () => {
    try {
      setLoading(true);
      const typesToFetch = allowedTypes.filter((t) => typeFilter === 'all' || t === typeFilter);
      const params = { limit: 1000 };
      if (searchTerm && searchTerm.trim()) params.search = searchTerm.trim();

      const results = await Promise.all(
        typesToFetch.map(async (type) => {
          try {
            const res = await TYPE_META[type].fetch(params);
            if (res && res.success && Array.isArray(res.data)) {
              return res.data.map((it) => ({ ...it, __type: type }));
            }
          } catch (error) {
            // Skip a type that fails to load; other types still render.
          }
          return [];
        })
      );

      const merged = results
        .flat()
        .sort((a, b) => new Date(b.deletedAt || 0) - new Date(a.deletedAt || 0));

      setAllItems(merged);
      setCurrentPage(1);
    } catch (error) {
      toast.error('Error loading trash: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setSearchTerm(value), 500);
  };

  const handleTypeChange = (e) => {
    setTypeFilter(e.target.value);
    setCurrentPage(1);
  };

  const getItemId = (item) => item._id || item.id;

  const handleRestore = async (item) => {
    // Projects: offer the user a choice of which trashed tasks to restore.
    if (item.__type === 'project') {
      setItemToAction(item);
      setProjectTrashedTasks([]);
      setSelectedTaskIds([]);
      setLoadingProjectTasks(true);
      setShowProjectRestoreModal(true);
      try {
        const res = await api.getProjectTrashedTasks(getItemId(item));
        const tasks = res && res.success && Array.isArray(res.data) ? res.data : [];
        if (tasks.length === 0) {
          // No tasks were trashed with this project — show a plain restore
          // confirmation instead of restoring silently.
          setShowProjectRestoreModal(false);
          setShowRestoreModal(true);
          return;
        }
        setProjectTrashedTasks(tasks);
        setSelectedTaskIds(tasks.map((t) => t._id || t.id));
      } catch (error) {
        toast.error('Error loading project tasks: ' + (error.message || 'Unknown error'));
        setShowProjectRestoreModal(false);
        setItemToAction(null);
      } finally {
        setLoadingProjectTasks(false);
      }
      return;
    }
    setItemToAction(item);
    setShowRestoreModal(true);
  };

  const handlePermanentDelete = (item) => {
    setItemToAction(item);
    setShowDeleteModal(true);
  };

  const confirmRestore = async () => {
    if (!itemToAction) return;
    const meta = TYPE_META[itemToAction.__type];
    try {
      setProcessing(true);
      const response = await meta.restore(getItemId(itemToAction));
      if (response.success) {
        toast.success(`${capitalize(meta.itemNoun)} restored successfully!`);
        setShowRestoreModal(false);
        setItemToAction(null);
        fetchTrash();
      } else {
        toast.error(response.message || `Error restoring ${meta.itemNoun}`);
      }
    } catch (error) {
      toast.error(`Error restoring ${meta.itemNoun}: ` + (error.message || 'Unknown error'));
    } finally {
      setProcessing(false);
    }
  };

  const confirmPermanentDelete = async () => {
    if (!itemToAction) return;
    const meta = TYPE_META[itemToAction.__type];
    try {
      setProcessing(true);
      const response = await meta.remove(getItemId(itemToAction));
      if (response.success) {
        toast.success(`${capitalize(meta.itemNoun)} permanently deleted!`);
        setShowDeleteModal(false);
        setItemToAction(null);
        fetchTrash();
      } else {
        toast.error(response.message || `Error deleting ${meta.itemNoun}`);
      }
    } catch (error) {
      toast.error(`Error deleting ${meta.itemNoun}: ` + (error.message || 'Unknown error'));
    } finally {
      setProcessing(false);
    }
  };

  // Restore a project, optionally limiting which trashed tasks come back.
  // `taskIds` undefined => restore all; an array => restore only those.
  const restoreProjectWithTasks = async (item, taskIds) => {
    try {
      setProcessing(true);
      const response = await api.restoreProject(getItemId(item), taskIds);
      if (response.success) {
        const restoredCount = Array.isArray(taskIds) ? taskIds.length : null;
        toast.success(
          restoredCount !== null
            ? `Project restored with ${restoredCount} task${restoredCount !== 1 ? 's' : ''}!`
            : 'Project restored successfully!'
        );
        setShowProjectRestoreModal(false);
        setItemToAction(null);
        setProjectTrashedTasks([]);
        setSelectedTaskIds([]);
        fetchTrash();
      } else {
        toast.error(response.message || 'Error restoring project');
      }
    } catch (error) {
      toast.error('Error restoring project: ' + (error.message || 'Unknown error'));
    } finally {
      setProcessing(false);
    }
  };

  // Permanently delete every item currently in the trash list (honours the active
  // type/search filter — the confirmation states the exact count). Each item is
  // removed via its own type's delete endpoint; failures are reported, not thrown.
  const confirmEmptyTrash = async () => {
    if (!allItems.length) {
      setShowEmptyTrashModal(false);
      return;
    }
    try {
      setEmptying(true);
      const results = await Promise.allSettled(
        allItems.map((item) => TYPE_META[item.__type].remove(getItemId(item)))
      );
      const failed = results.filter(
        (r) => r.status === 'rejected' || (r.value && r.value.success === false)
      ).length;
      const deleted = allItems.length - failed;
      if (deleted > 0) {
        toast.success(`Trash emptied — ${deleted} item${deleted !== 1 ? 's' : ''} permanently deleted`);
      }
      if (failed > 0) {
        toast.error(`${failed} item${failed !== 1 ? 's' : ''} could not be deleted`);
      }
      setShowEmptyTrashModal(false);
      fetchTrash();
    } catch (error) {
      toast.error('Error emptying trash: ' + (error.message || 'Unknown error'));
    } finally {
      setEmptying(false);
    }
  };

  const confirmProjectRestore = () => {
    if (!itemToAction) return;
    restoreProjectWithTasks(itemToAction, selectedTaskIds);
  };

  const toggleTaskSelection = (taskId) => {
    setSelectedTaskIds((prev) =>
      prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId]
    );
  };

  // Client-side pagination over the merged list.
  const totalItems = allItems.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const pageItems = allItems.slice((safePage - 1) * itemsPerPage, safePage * itemsPerPage);

  if (!permissionsReady) {
    return <Loader />;
  }
  if (!allowTrashPage) {
    return null;
  }

  const typeMeta = itemToAction ? TYPE_META[itemToAction.__type] : null;

  return (
    <div className="trash-page" style={{ padding: '16px' }}>
      {/* Filters: description search + type dropdown */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search by description (asset, project or task name)..."
          defaultValue={searchTerm}
          onChange={handleSearchChange}
          style={{ flex: 1, minWidth: '240px', padding: '10px 12px', border: '2px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }}
        />
        <select
          value={typeFilter}
          onChange={handleTypeChange}
          style={{ padding: '10px 12px', border: '2px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', fontWeight: 600, color: '#2d3748', background: 'white', cursor: 'pointer', minWidth: '150px' }}
        >
          <option value="all">All Types</option>
          {allowedTypes.map((type) => (
            <option key={type} value={type}>{TYPE_META[type].labelPlural}</option>
          ))}
        </select>
        {totalItems > 0 && (
          <button
            type="button"
            onClick={() => setShowEmptyTrashModal(true)}
            disabled={processing || emptying}
            title="Permanently delete everything in this list"
            style={{
              padding: '10px 16px',
              background: '#fff5f5',
              color: '#c53030',
              border: '2px solid #fed7d7',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: processing || emptying ? 'not-allowed' : 'pointer',
              opacity: processing || emptying ? 0.6 : 1,
              whiteSpace: 'nowrap',
            }}
          >
            🗑️ Empty Trash
          </button>
        )}
      </div>

      {loading && allItems.length === 0 ? (
        <Loader />
      ) : totalItems === 0 ? (
        <div style={{ background: 'white', padding: '60px 20px', borderRadius: '8px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>🗑️</div>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600, color: '#2d3748' }}>Trash is Empty</h3>
          <p style={{ margin: 0, color: '#718096', fontSize: '14px' }}>
            {searchTerm || typeFilter !== 'all'
              ? 'No deleted items match your filters.'
              : 'Nothing has been deleted yet.'}
          </p>
        </div>
      ) : (
        <>
          <div style={{ background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                  <th style={{ ...thStyle, width: '60px' }}>S.No</th>
                  <th style={thStyle}>Description</th>
                  <th style={{ ...thStyle, width: '110px' }}>Type</th>
                  <th style={{ ...thStyle, width: '180px' }}>Date Deleted</th>
                  <th style={{ ...thStyle, width: '180px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((item, index) => {
                  const meta = TYPE_META[item.__type];
                  const serial = (safePage - 1) * itemsPerPage + index + 1;
                  return (
                    <tr
                      key={`${item.__type}-${getItemId(item)}`}
                      style={{ borderBottom: '1px solid #e2e8f0', transition: 'background 0.15s ease', background: 'white' }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = '#f7fafc')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'white')}
                    >
                      <td style={{ padding: '12px', fontSize: '14px', color: '#718096' }}>{serial}</td>
                      <td style={{ padding: '12px', fontSize: '14px', color: '#2d3748', fontWeight: 600 }}>
                        {meta.getDescription(item)}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px' }}>{typeBadge(item.__type)}</td>
                      <td style={{ padding: '12px', fontSize: '14px', color: '#718096' }}>{formatDate(item.deletedAt)}</td>
                      <td style={{ padding: '12px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            type="button"
                            onClick={() => handleRestore(item)}
                            disabled={processing}
                            title="Restore"
                            aria-label="Restore"
                            style={iconBtn('#f0fff4', '#276749', processing)}
                          >
                            ♻️
                          </button>
                          <button
                            type="button"
                            onClick={() => handlePermanentDelete(item)}
                            disabled={processing}
                            title="Delete permanently"
                            aria-label="Delete permanently"
                            style={iconBtn('#fff5f5', '#c53030', processing)}
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div style={{ marginTop: '20px' }}>
              <Pagination
                currentPage={safePage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                itemsPerPage={itemsPerPage}
                totalItems={totalItems}
                onItemsPerPageChange={setItemsPerPage}
              />
            </div>
          )}
        </>
      )}

      {/* Restore Confirmation Modal (assets & tasks) */}
      <ConfirmActionModal
        isOpen={showRestoreModal}
        onClose={() => { setShowRestoreModal(false); setItemToAction(null); }}
        onConfirm={confirmRestore}
        title={`Restore ${typeMeta ? capitalize(typeMeta.itemNoun) : 'Item'}`}
        message={`Are you sure you want to restore "${typeMeta ? typeMeta.getTitle(itemToAction) : ''}"? This will make the ${typeMeta ? typeMeta.itemNoun : 'item'} available again.`}
        confirmText="Restore"
        cancelText="Cancel"
        confirmColor="#38a169"
        loading={processing}
      />

      {/* Permanent Delete Confirmation Modal */}
      <ConfirmActionModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setItemToAction(null); }}
        onConfirm={confirmPermanentDelete}
        title={`Permanently Delete ${typeMeta ? capitalize(typeMeta.itemNoun) : 'Item'}`}
        message={`Are you sure you want to permanently delete "${typeMeta ? typeMeta.getTitle(itemToAction) : ''}"? This action cannot be undone.`}
        confirmText="Delete Permanently"
        cancelText="Cancel"
        confirmColor="#e53e3e"
        loading={processing}
      />

      {/* Empty Trash Confirmation Modal */}
      <ConfirmActionModal
        isOpen={showEmptyTrashModal}
        onClose={() => { if (!emptying) setShowEmptyTrashModal(false); }}
        onConfirm={confirmEmptyTrash}
        title="Empty Trash"
        message={`Are you sure you want to permanently delete all ${totalItems} item${totalItems !== 1 ? 's' : ''} in this list? This action cannot be undone.`}
        confirmText="Empty Trash"
        cancelText="Cancel"
        confirmColor="#e53e3e"
        loading={emptying}
      />

      {/* Project Restore — task selection modal */}
      {showProjectRestoreModal && (
        <div
          className="modal-overlay"
          onClick={() => { if (!processing) { setShowProjectRestoreModal(false); setItemToAction(null); } }}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '560px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#2d3748' }}>Restore Project</h2>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => { if (!processing) { setShowProjectRestoreModal(false); setItemToAction(null); } }}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {loadingProjectTasks ? (
              <div style={{ padding: '30px 0' }}><Loader /></div>
            ) : (
              <>
                <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#4a5568', lineHeight: '1.5' }}>
                  Restoring <strong>"{itemToAction ? TYPE_META.project.getTitle(itemToAction) : ''}"</strong> will bring back{' '}
                  <strong>{projectTrashedTasks.length}</strong> task{projectTrashedTasks.length !== 1 ? 's' : ''} that{' '}
                  {projectTrashedTasks.length !== 1 ? 'were' : 'was'} deleted with it. Choose which tasks to restore —
                  the rest will stay in Trash.
                </p>

                {/* Select all */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#f7fafc', borderRadius: '6px', marginBottom: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#2d3748' }}>
                    <input
                      type="checkbox"
                      checked={selectedTaskIds.length === projectTrashedTasks.length && projectTrashedTasks.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedTaskIds(projectTrashedTasks.map((t) => t._id || t.id));
                        else setSelectedTaskIds([]);
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                    Select All
                  </label>
                  <span style={{ fontSize: '12px', color: '#718096' }}>
                    {selectedTaskIds.length} of {projectTrashedTasks.length} selected
                  </span>
                </div>

                {/* Task list */}
                <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '6px', marginBottom: '20px' }}>
                  {projectTrashedTasks.map((t) => {
                    const taskId = t._id || t.id;
                    const checked = selectedTaskIds.includes(taskId);
                    return (
                      <label
                        key={taskId}
                        style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderBottom: '1px solid #edf2f7', cursor: 'pointer', background: checked ? '#ebf8ff' : 'white' }}
                      >
                        <input type="checkbox" checked={checked} onChange={() => toggleTaskSelection(taskId)} style={{ cursor: 'pointer' }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '13px', fontWeight: 600, color: '#2d3748', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {t.taskCode ? `${t.taskCode} · ` : ''}{t.name}
                          </div>
                          <div style={{ fontSize: '11px', color: '#718096' }}>
                            {statusBadge(t.status)} <span style={{ marginLeft: '6px' }}>Deleted {formatDate(t.deletedAt)}</span>
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </>
            )}

            <div className="modal-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => { setShowProjectRestoreModal(false); setItemToAction(null); }}
                disabled={processing}
                style={{ padding: '10px 20px', background: '#e2e8f0', color: '#4a5568', border: 'none', borderRadius: '6px', cursor: processing ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '13px' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmProjectRestore}
                disabled={processing || loadingProjectTasks}
                style={{ padding: '10px 20px', background: '#38a169', color: 'white', border: 'none', borderRadius: '6px', cursor: (processing || loadingProjectTasks) ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '13px', opacity: (processing || loadingProjectTasks) ? 0.6 : 1 }}
              >
                {processing
                  ? 'Restoring...'
                  : `Restore Project + ${selectedTaskIds.length} Task${selectedTaskIds.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const thStyle = { padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#718096' };

const iconBtn = (bg, color, processing) => ({
  padding: '6px 10px',
  background: bg,
  color,
  border: 'none',
  borderRadius: '4px',
  cursor: processing ? 'not-allowed' : 'pointer',
  fontSize: '15px',
  lineHeight: 1,
  opacity: processing ? 0.6 : 1,
});

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export default TrashPage;
